'use client'

import Image from 'next/image'
import Link from 'next/link'
import { BellRing, ChevronDown, Clock3, List, Map, MapPin, Search, SlidersHorizontal, UtensilsCrossed } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet'
import { BookingSheet } from './booking-sheet'
import { createBookingLaunch } from './booking-launch'
import { CustomerHeader } from './customer-header'
import { FavoriteVenueButton } from './favorite-venue-button'
import { GuestStepper, ReservationDatePicker, ReservationTimePicker } from './reservation-controls'
import { LocationCombobox } from './location-combobox'
import type { AvailabilityOption, BookingIntent, DiscoverySearchQuery, PublicOffering, PublicVenue, PublicVenueCatalogState, Venue } from './domain'
import { defaultVenueImage, toBookingVenue } from './venue-presentation'
import { useFavoriteLocations } from './use-favorite-locations'

type AvailabilityState = {
  status: 'loading' | 'ready' | 'error'
  options: AvailabilityOption[]
  message?: string
}

type SelectedBooking = {
  venue: Venue
  intent: BookingIntent
  initialOption?: AvailabilityOption
}

export function SearchResults({ catalog, query, locationOptions }: { catalog: PublicVenueCatalogState; query: DiscoverySearchQuery; locationOptions: string[] }) {
  const [availability, setAvailability] = useState<Record<string, AvailabilityState>>({})
  const [availabilityOnly, setAvailabilityOnly] = useState(false)
  const [windowMinutes, setWindowMinutes] = useState<60 | 120 | 720>(120)
  const [mobileView, setMobileView] = useState<'list' | 'map'>('list')
  const [selectedBooking, setSelectedBooking] = useState<SelectedBooking | null>(null)
  const [searchDate, setSearchDate] = useState(query.date)
  const [searchTime, setSearchTime] = useState(query.time)
  const [searchPartySize, setSearchPartySize] = useState(query.partySize)
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false)
  const mobileSearchSheetRef = useRef<HTMLDivElement>(null)
  const favoriteLocations = useFavoriteLocations()

  useEffect(() => {
    if (catalog.status !== 'ready') return
    const controller = new AbortController()
    const entries = catalog.venues.flatMap((venue) => venue.offerings.map((offering) => [offeringKey(venue, offering), venue, offering] as const))
    setAvailability(Object.fromEntries(entries.map(([key]) => [key, { status: 'loading', options: [] }])))

    const load = async () => {
      try {
        const response = await fetch('/api/tables/availability/search-many', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ searches: entries.map(([key, venue, offering]) => ({
            requestId: key,
            tenantSlug: venue.tenantSlug,
            locationSlug: venue.locationSlug,
            experienceId: offering.id,
            partySize: query.partySize,
            dateFrom: query.date,
            dateTo: query.date,
          })) }),
          signal: controller.signal,
        })
        const payload = await response.json()
        if (!response.ok) throw new Error(payload.message ?? 'Availability could not be loaded.')
        const next = Object.fromEntries((payload.results ?? []).map((result: {
          requestId: string
          status: number
          availability?: { dates?: Array<{ options?: AvailabilityOption[] }> }
          error?: { message?: string }
        }) => [result.requestId, result.status === 200
          ? { status: 'ready', options: result.availability?.dates?.[0]?.options ?? [] }
          : { status: 'error', options: [], message: result.error?.message ?? 'Availability could not be loaded.' }]))
        if (!controller.signal.aborted) setAvailability(next)
      } catch (cause) {
        if (controller.signal.aborted) return
        setAvailability(Object.fromEntries(entries.map(([key]) => [key, {
          status: 'error',
          options: [],
          message: cause instanceof Error ? cause.message : 'Availability could not be loaded.',
        }])))
      }
    }

    if (entries.length > 0) void load()
    return () => controller.abort()
  }, [catalog, query.date, query.partySize])

  const marketMatches = matchesMarket(query.location, catalog.market)
  const visibleVenues = useMemo(() => {
    const venues = catalog.status === 'ready' ? catalog.venues : []
    if (!marketMatches) return []
    if (!availabilityOnly) return venues
    return venues.filter((venue) => venue.offerings.some((offering) => {
      const state = availability[offeringKey(venue, offering)]
      if (!state || state.status === 'loading') return true
      return nearbyOptions(state.options, query.time, windowMinutes).length > 0
    }))
  }, [availability, availabilityOnly, catalog, marketMatches, query.time, windowMinutes])

  function openBooking(venue: PublicVenue, offering: PublicOffering, option?: AvailabilityOption, source?: 'notify') {
    const launch = createBookingLaunch({
      locationSlug: venue.locationSlug,
      offering,
      partySize: query.partySize,
      date: query.date,
      option,
      source,
      accessToken: venue.accessToken,
    })
    setSelectedBooking({
      venue: toBookingVenue(venue, offering, catalog.market),
      ...launch,
    })
  }

  return (
    <div className="search-page">
      <CustomerHeader surface="solid" />
      <main>
        <section className="search-toolbar" aria-label="Reservation search controls">
          <div className="mobile-search-summary">
            <button className="mobile-search-summary-values" type="button" onClick={() => setMobileSearchOpen(true)}>
              <span>{formatCompactDate(searchDate)}</span><span>{formatClock(searchTime)}</span><span>{searchPartySize} {searchPartySize === 1 ? 'guest' : 'guests'}</span>
            </button>
            <button className="mobile-search-edit" type="button" aria-label="Adjust your search" onClick={() => setMobileSearchOpen(true)}><SlidersHorizontal /></button>
          </div>
          <form className="desktop-search-form" action="/search" method="get">
            <label className="search-filter-field search-filter-type"><UtensilsCrossed /><span><small>Experience</small><select name="type" defaultValue={query.type} aria-label="Experience type"><option value="dine-in">Dine in</option></select></span><ChevronDown /></label>
            <LocationCombobox className="search-filter-field search-filter-location" variant="toolbar" value={query.location} suggestions={locationOptions} />
            <div className="search-filter-date"><ReservationDatePicker className="search-filter-field" name="date" value={searchDate} min={todayDate()} onChange={setSearchDate} /></div>
            <div className="search-filter-time"><ReservationTimePicker className="search-filter-field" name="time" value={searchTime} onChange={setSearchTime} /></div>
            <GuestStepper className="search-filter-field search-filter-party" label="Party" name="partySize" value={searchPartySize} max={12} onChange={setSearchPartySize} />
            <button className="search-update" type="submit"><Search /> <span>Update search</span></button>
          </form>
        </section>

        <section className="search-results-heading">
          <div>
            <h1>{visibleVenues.length} {visibleVenues.length === 1 ? 'place' : 'places'} near {query.location}</h1>
            <p>{formatSearchSummary(query)} · Times shown closest to your search.</p>
          </div>
          <div className="search-heading-actions">
            <div className="mobile-results-toggle" aria-label="Results view">
              <button className={mobileView === 'list' ? 'active' : ''} onClick={() => setMobileView('list')}><List /> List</button>
              <button className={mobileView === 'map' ? 'active' : ''} onClick={() => setMobileView('map')}><Map /> Map</button>
            </div>
            <Dialog>
              <DialogTrigger asChild><button className="more-filters"><SlidersHorizontal /> More filters{availabilityOnly || windowMinutes !== 120 ? <span /> : null}</button></DialogTrigger>
              <DialogContent className="search-filter-dialog">
                <DialogHeader><DialogTitle>More filters</DialogTitle><DialogDescription>Choose which restaurants and times to show.</DialogDescription></DialogHeader>
                <fieldset><legend>Availability</legend><label><input type="radio" name="availability" checked={!availabilityOnly} onChange={() => setAvailabilityOnly(false)} /> Show all restaurants</label><label><input type="radio" name="availability" checked={availabilityOnly} onChange={() => setAvailabilityOnly(true)} /> Available near my time</label></fieldset>
                <fieldset><legend>Time window</legend><label><input type="radio" name="window" checked={windowMinutes === 60} onChange={() => setWindowMinutes(60)} /> Within 1 hour</label><label><input type="radio" name="window" checked={windowMinutes === 120} onChange={() => setWindowMinutes(120)} /> Within 2 hours</label><label><input type="radio" name="window" checked={windowMinutes === 720} onChange={() => setWindowMinutes(720)} /> Any time that day</label></fieldset>
                <DialogFooter showCloseButton />
              </DialogContent>
            </Dialog>
          </div>
        </section>

        <div className={`search-results-layout search-results-layout--${mobileView}`}>
          <section className="search-results-list" aria-live="polite">
            {catalog.status === 'unavailable' && <SearchUnavailable message={catalog.message} />}
            {catalog.status === 'ready' && visibleVenues.length === 0 && <SearchEmpty location={query.location} market={catalog.market} />}
            {visibleVenues.map((venue) => (
              <article className="search-venue-result" key={venue.publicWidgetKey}>
                <header>
                  <div><p>{venue.neighborhood || venue.market || catalog.market}</p><h2><Link href={restaurantHref(venue)}>{venue.publicName}</Link></h2><small>{[venue.priceRange, venue.cuisine, venue.distanceKm === undefined ? null : `${venue.distanceKm.toFixed(1)} km`].filter(Boolean).join(' · ')}</small><FavoriteVenueButton venueName={venue.publicName} active={favoriteLocations.isFavorite(venue.locationSlug)} busy={favoriteLocations.pending === venue.locationSlug} onToggle={() => void favoriteLocations.toggle(venue.locationSlug)} /></div>
                  <Link className="search-venue-image" href={restaurantHref(venue)} aria-label={`View ${venue.publicName}`}><Image src={venue.imageUrl || defaultVenueImage} alt={`${venue.publicName} restaurant`} fill sizes="(max-width: 720px) 38vw, 220px" /></Link>
                </header>
                <div className="search-offering-list">
                  {venue.offerings.map((offering) => {
                    const key = offeringKey(venue, offering)
                    const state = availability[key] ?? { status: 'loading', options: [] }
                    const options = nearbyOptions(state.options, query.time, windowMinutes)
                    return (
                      <div className="search-offering" key={key}>
                        <button className="search-offering-title" onClick={() => openBooking(venue, offering)} aria-label={`View all times for ${offering.name}`}><strong>{offering.name}</strong><span>For {query.partySize} {query.partySize === 1 ? 'guest' : 'guests'}</span></button>
                        {state.status === 'loading' && <div className="search-time-skeleton" aria-label="Loading availability"><i /><i /><i /></div>}
                        {state.status === 'error' && <div className="search-offering-status"><span>Availability could not be loaded.</span><button onClick={() => openBooking(venue, offering)}>View offering</button></div>}
                        {state.status === 'ready' && options.length > 0 && <div className="search-result-times">{options.map((option) => {
                          const bookable = option.bookable && Boolean(option.optionToken)
                          return <button className={bookable ? '' : 'is-notify'} key={option.startsAt} aria-label={`${bookable ? 'Book' : 'Get notified for'} ${offering.name} at ${formatTime(option.startsAt)}`} onClick={() => openBooking(venue, offering, option, bookable ? undefined : 'notify')}><span>{formatTime(option.startsAt)}</span><strong>{bookable ? 'Book' : 'Notify'}</strong></button>
                        })}</div>}
                        {state.status === 'ready' && options.length === 0 && <div className="search-offering-status"><span>No matching tables near {formatClock(query.time)}.</span><button onClick={() => openBooking(venue, offering, undefined, 'notify')}><BellRing /> Get notified</button></div>}
                      </div>
                    )
                  })}
                </div>
              </article>
            ))}
            {catalog.status === 'ready' && catalog.pagination.totalPages > 1 && (
              <nav className="search-pagination" aria-label="Restaurant result pages">
                {catalog.pagination.page > 1
                  ? <Link href={searchPageHref(query, catalog.pagination.page - 1)}>Previous</Link>
                  : <span aria-disabled="true">Previous</span>}
                <small>Page {catalog.pagination.page} of {catalog.pagination.totalPages}</small>
                {catalog.pagination.page < catalog.pagination.totalPages
                  ? <Link href={searchPageHref(query, catalog.pagination.page + 1)}>Next</Link>
                  : <span aria-disabled="true">Next</span>}
              </nav>
            )}
          </section>

          <aside className="search-map" aria-label={`Map of ${query.location}`}>
            <iframe title={`Map of ${query.location}`} src={`https://www.google.com/maps?q=${encodeURIComponent(query.location)}&z=11&output=embed`} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
            <div className="search-map-caption"><MapPin /><span><strong>{visibleVenues.length} {visibleVenues.length === 1 ? 'restaurant' : 'restaurants'}</strong><small>{query.location}</small></span></div>
          </aside>
        </div>
      </main>

      {selectedBooking && <BookingSheet venue={selectedBooking.venue} initialSearch={selectedBooking.intent} initialOption={selectedBooking.initialOption} onClose={() => setSelectedBooking(null)} />}
      <Sheet open={mobileSearchOpen} onOpenChange={setMobileSearchOpen}>
        <SheetContent ref={mobileSearchSheetRef} side="bottom" className="mobile-search-sheet" showCloseButton>
          <SheetTitle>Adjust your search</SheetTitle>
          <form action="/search" method="get">
            <input type="hidden" name="type" value={query.type} />
            <LocationCombobox className="mobile-search-location" variant="mobile" value={query.location} suggestions={locationOptions} portalContainer={mobileSearchSheetRef} />
            <input type="hidden" name="date" value={searchDate} />
            <div className="mobile-search-calendar"><Calendar mode="single" selected={localDateValue(searchDate)} disabled={{ before: localDateValue(todayDate()) }} onSelect={(nextDate) => { if (nextDate) setSearchDate(toLocalDate(nextDate)) }} /></div>
            <div className="mobile-search-fields">
              <ReservationTimePicker className="mobile-search-field" name="time" value={searchTime} onChange={setSearchTime} />
              <GuestStepper className="mobile-search-field" label="Party size" name="partySize" value={searchPartySize} max={12} onChange={setSearchPartySize} />
            </div>
            <Button type="submit" className="button button--burgundy button--full mobile-search-submit">Update search</Button>
          </form>
        </SheetContent>
      </Sheet>
    </div>
  )
}

function SearchUnavailable({ message }: { message: string }) {
  return <div className="search-empty"><Clock3 /><h2>Reservations are unavailable.</h2><p>{message}</p><Button asChild className="button button--burgundy"><Link href="/">Return home</Link></Button></div>
}

function SearchEmpty({ location, market }: { location: string; market: string }) {
  return <div className="search-empty"><MapPin /><h2>No restaurants found in {location}.</h2><p>Try {market} or change the location above.</p><Button asChild className="button button--burgundy"><Link href={`/search?location=${encodeURIComponent(market)}`}>Search {market}</Link></Button></div>
}

function offeringKey(venue: PublicVenue, offering: PublicOffering) {
  return `${venue.publicWidgetKey}:${offering.id}`
}

function matchesMarket(location: string, market: string) {
  const requested = location.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()
  const published = market.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()
  const city = published.split(' ')[0]
  return !requested || requested === 'near me' || requested.includes(city) || published.includes(requested)
}

function nearbyOptions(options: AvailabilityOption[], requestedTime: string, windowMinutes: number) {
  const target = toMinutes(requestedTime)
  return options
    .map((option) => ({ option, distance: Math.abs(toMinutes(option.localStart) - target) }))
    .filter(({ distance }) => distance <= windowMinutes)
    .sort((left, right) => left.distance - right.distance)
    .slice(0, 4)
    .sort((left, right) => left.option.startsAt.localeCompare(right.option.startsAt))
    .map(({ option }) => option)
}

function toMinutes(value: string) {
  const match = value.match(/(\d{1,2}):(\d{2})/)
  return match ? Number(match[1]) * 60 + Number(match[2]) : 0
}

function formatSearchSummary(query: DiscoverySearchQuery) {
  const date = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }).format(new Date(`${query.date}T12:00:00Z`))
  return `${date} at ${formatClock(query.time)} for ${query.partySize} ${query.partySize === 1 ? 'guest' : 'guests'}`
}

function formatClock(value: string) {
  const [hours, minutes] = value.split(':').map(Number)
  return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'UTC' }).format(new Date(Date.UTC(2026, 0, 1, hours, minutes)))
}

function formatCompactDate(value: string) {
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }).format(new Date(`${value}T12:00:00Z`))
}

function localDateValue(value: string) {
  return new Date(`${value}T12:00:00`)
}

function toLocalDate(date: Date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function formatTime(value: string) {
  return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/Los_Angeles' }).format(new Date(value))
}

function todayDate() {
  const date = new Date()
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function searchPageHref(query: DiscoverySearchQuery, page: number) {
  const params = new URLSearchParams({
    type: query.type,
    location: query.location,
    date: query.date,
    time: query.time,
    partySize: String(query.partySize),
    page: String(page),
  })
  return `/search?${params}`
}

function restaurantHref(venue: PublicVenue) {
  return `/restaurants/${encodeURIComponent(venue.tenantSlug)}/${encodeURIComponent(venue.locationSlug)}`
}
